import { config } from '@vartion/ui'
import { debounce } from 'lodash-es'
import { acceptHMRUpdate, defineStore } from 'pinia'

import { setLocale } from '~/services/i18n.ts'
import { type OrganizationDefaultSettings, type UserSettings } from '~/types.ts'

const save = debounce(
  async function (settings: UserSettings) {
    await stores.loading.update({ settings: true })
    await api.post('settings', settings)
    await stores.loading.update({ settings: false })
  },
  2000,
  { leading: true },
)

function setNumberFormat(format: string) {
  if (format === 'en-gb') {
    config.VInput.thousandSeparator = ','
    config.VInput.decimalSeparator = '.'
  } else if (format === 'nl') {
    config.VInput.thousandSeparator = '.'
    config.VInput.decimalSeparator = ','
  } else {
    config.VInput.thousandSeparator = ''
    config.VInput.decimalSeparator = '.'
  }
}

export const useSettings = defineStore('settings', () => {
  const settings = reactive<UserSettings>({
    application_update_email: true,
    send_empty_monitor_report: true,
    show_live_chat: true,
    summary_report_day_of_week: 1,
    summary_report_email: true,
    summary_report_interval: 'weekly',
    translation_language: 'en',
    interface_language: 'en-gb',
    known_languages: ['en'],
    date_format: 'DD/MM/YYYY',
    number_format: 'en-gb',
    notify_hit_updates: 'never',
    require_one_time_password: false,
    receive_transaction_reports_via_email: false,
    receive_transaction_reports_without_findings: false,
    comment_mentions_email_frequency: 'daily',
  })

  const organizationDefaultSettings = reactive<OrganizationDefaultSettings>({
    translation_language: 'en',
    interface_language: 'en-gb',
    known_languages: ['en'],
    date_format: 'DD/MM/YYYY',
  })

  watch(
    () => settings.date_format,
    (value) => {
      config.VDatePicker.displayFormat.default = value
      config.VDatePicker.displayFormat.seconds = `${value} HH:mm:ss`
    },
  )

  watch(() => settings.number_format, setNumberFormat)

  async function load() {
    const userSettings = stores.user.user.settings
    // If the user has customized their settings, an ID is present.
    if (userSettings.id) {
      Object.assign(settings, userSettings)
    } else {
      Object.assign(settings, organizationDefaultSettings)
    }

    config.VDatePicker.displayFormat.default = settings.date_format
    config.VDatePicker.displayFormat.seconds = `${settings.date_format} HH:mm:ss`
    setNumberFormat(settings.number_format)
    await setLocale(settings.interface_language)
  }

  async function update(updates?: Partial<UserSettings>) {
    await stores.loading.update({ settings: true })

    if (updates !== undefined) {
      Object.assign(settings, updates)
      setLocale(settings.interface_language)
    }

    try {
      await save({ ...organizationDefaultSettings, ...settings })
    } catch {
      $message.error($t('api-error'))
    }
  }

  async function updateDefaultSettings(defaultSettings: OrganizationDefaultSettings) {
    try {
      const { data } = await api.patch(`organization-default-settings/${stores.organization.id}`, defaultSettings)
      Object.assign(organizationDefaultSettings, data)
      await load()
    } catch {
      $message.error($t('api-error'))
    }
  }

  return {
    settings,
    organizationDefaultSettings,
    load,
    update,
    updateDefaultSettings,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSettings, import.meta.hot))
}
